/**
 * CierreMuelle — Cuadre de caja del día (versión completa con P1+P2+P3).
 *
 * Replica funcionalmente la planilla física de Entretur:
 *   - Encabezado: vendedor + punto + hora apertura
 *   - REVERSIONES (anulaciones del día)
 *   - RECAUDO por franquicia (VISA/MC/AMEX/...) + efectivo
 *   - RESERVAS por canal (internet/aliados/comercial)
 *   - PENDIENTES POR COBRAR (agencias = aliados)
 *   - CUADRE: sistema vs físico + diferencia
 *   - CONTEO DE DENOMINACIONES (auto-totaliza efectivo)
 *   - FIRMA CAJERO + Vo.Bo. SUPERVISOR
 *   - Bonus: detalle por salida (acordeones)
 *   - Generar PDF (backend ReportLab, replica planilla)
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, IconButton, Chip, Button,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
  useMediaQuery, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import HistoryIcon from '@mui/icons-material/History';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const DENOMINACIONES = [100000, 50000, 20000, 10000, 5000, 2000];

function getEmpresaId() {
  const a = localStorage.getItem('empresa_activa');
  if (a) return a;
  try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; }
}
function getUserName() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').name || ''; } catch { return ''; }
}
function getUserId() {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.user_id || null; } catch { return null; }
}
function getUserRole() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').role || 'miembro'; } catch { return 'miembro'; }
}
function authHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmt(n) {
  if (n == null) return '$0';
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Math.round(Number(n)));
}
function fmtPax(r) {
  const parts = [];
  if (r.adultos) parts.push(`${r.adultos}A`);
  if (r.ninos) parts.push(`${r.ninos}N`);
  if (r.bebes) parts.push(`${r.bebes}B`);
  return parts.join(' · ') || '—';
}

const TIPO_VENTA_LABEL = {
  directa:          { label: 'Directa',      color: '#0d9488', bg: '#ccfbf1' },
  walkin:           { label: 'Walk-in',      color: '#7c3aed', bg: '#ede9fe' },
  agencia_anticipo: { label: 'Ag. + anticipo', color: '#0ea5e9', bg: '#dbeafe' },
  agencia_cupo:     { label: 'Cupo crédito', color: '#dc2626', bg: '#fee2e2' },
};

export default function CierreMuelle() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const empresaId = getEmpresaId();
  const { toast, ToastContainer } = useToast();
  const [fecha, setFecha] = useState(() => todayLocal());

  // Datos del cierre del día (sistema)
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['cierre-muelle', empresaId, fecha],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/turismo/muelle/cierre?empresa_id=${empresaId}&fecha=${fecha}`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Error cargando cierre');
      return r.json();
    },
  });

  // Estado editable del cierre formal
  const [vendedorNombre, setVendedorNombre] = useState('');
  const [fisico, setFisico] = useState({ efectivo: 0, tarjetas: 0, bold: 0, transferencia: 0, otros: 0 });
  const [denoms, setDenoms] = useState({});
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  // Diálogos
  const [revOpen, setRevOpen] = useState(false);
  const [aprobarOpen, setAprobarOpen] = useState(false);

  // Pre-cargar estado al cambiar fecha o data
  useEffect(() => {
    if (!data) return;
    const c = data.cierre_formal || {};
    setVendedorNombre(c.vendedor_nombre || getUserName());
    setNotas(c.notas || '');
    setDenoms(c.denominaciones || {});
    // Leer TODOS los campos físicos del snapshot — antes solo se leía efectivo
    // y los otros se hardcodeaban a 0, lo que hacía que el operador viera los
    // campos vacíos aunque en BD estaban grabados. Bug reportado 2026-07-03.
    setFisico({
      efectivo: Number(c.fisico_efectivo || 0),
      tarjetas: Number(c.fisico_tarjetas || 0),
      bold: Number(c.fisico_bold || 0),
      transferencia: Number(c.fisico_transferencia || 0),
      otros: Number(c.fisico_otros || 0),
    });
  }, [data?.cierre_formal, fecha]); // eslint-disable-line

  // Cálculo auto del efectivo según denominaciones
  const efectivoFromDenoms = useMemo(() => {
    return DENOMINACIONES.reduce((acc, d) => acc + (Number(denoms[d] || 0) * d), 0);
  }, [denoms]);

  // Si hay denominaciones, ese es el efectivo físico; si no, usar el campo manual
  const efectivoFisico = efectivoFromDenoms > 0 ? efectivoFromDenoms : Number(fisico.efectivo || 0);
  const fisicoTotal = efectivoFisico + Number(fisico.tarjetas || 0) + Number(fisico.bold || 0)
                     + Number(fisico.transferencia || 0) + Number(fisico.otros || 0);
  const sistemaTotal = Number(data?.totales_dia?.caja_fisica?.subtotal || 0);
  // Sistema neto = total cobrado − gastos del día. Es lo que el cajero
  // físicamente debe tener al cerrar.
  const gastosTotal = Number(data?.totales_dia?.gastos?.subtotal || 0);
  const sistemaNeto = sistemaTotal - gastosTotal;
  const diferencia = fisicoTotal - sistemaNeto;
  const cuadraExacto = Math.abs(diferencia) < 0.01;

  // Pasajeros que la recepción aún no ha dirimido. Bloquean la firma: es la
  // primera puerta del cierre, antes incluso del cuadre de caja.
  const pendientesRecepcion = data?.totales_dia?.pendientes_recepcion || [];
  const recepcionLista = pendientesRecepcion.length === 0;

  const cierre = data?.cierre_formal;
  const yaFirmado = cierre?.estado === 'firmado' || cierre?.estado === 'aprobado';
  const yaAprobado = cierre?.estado === 'aprobado';

  // Guarda el motivo estructurado por el que el backend bloqueó la firma
  // (pasajeros sin resolver o casillas descuadradas), para mostrarle al
  // cajero QUÉ le falta en vez de un botón gris sin explicación.
  const [bloqueo, setBloqueo] = React.useState(null);
  // Soporte que se está viendo en grande.
  const [voucherAbierto, setVoucherAbierto] = useState(null);

  const guardar = async (firmar = false) => {
    setSaving(true);
    try {
      const body = {
        fecha,
        vendedor_nombre: vendedorNombre,
        vendedor_user_id: getUserId(),
        hora_apertura: null,
        fisico_efectivo: efectivoFisico,
        fisico_tarjetas: Number(fisico.tarjetas || 0),
        fisico_bold: Number(fisico.bold || 0),
        fisico_transferencia: Number(fisico.transferencia || 0),
        fisico_otros: Number(fisico.otros || 0),
        denominaciones: denoms,
        notas,
        firmar_cajero: firmar,
        firma_cajero_nombre: vendedorNombre,
      };
      const r = await fetch(`${API_BASE}/turismo/muelle/cierre/guardar?empresa_id=${empresaId}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        const d = e.detail;
        // Los bloqueos nuevos llegan como objeto {code, message, ...}; los
        // antiguos como texto plano.
        if (d && typeof d === 'object' && d.code) {
          setBloqueo(d);
          throw new Error(d.message || 'No se puede firmar');
        }
        throw new Error(typeof d === 'string' ? d : 'Error');
      }
      setBloqueo(null);
      toast.success(firmar ? 'Cierre firmado' : 'Cierre guardado como borrador');
      refetch();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const aprobar = async (supervisorNombre, notasSup) => {
    if (!cierre?.id) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/muelle/cierre/${cierre.id}/aprobar`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ supervisor_nombre: supervisorNombre, notas: notasSup || null,
                                supervisor_user_id: getUserId() }),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success('Aprobado por supervisor');
      setAprobarOpen(false);
      refetch();
    } catch (e) { toast.error(e.message); }
  };

  const descargarPDF = () => {
    const url = `${API_BASE}/turismo/muelle/cierre/pdf?empresa_id=${empresaId}&fecha=${fecha}`;
    window.open(url, '_blank');
  };

  const descargarAnexoCSV = () => {
    const url = `${API_BASE}/turismo/muelle/cierre/anexo.csv?empresa_id=${empresaId}&fecha=${fecha}`;
    window.open(url, '_blank');
  };

  const descargarAnexoPDF = () => {
    const url = `${API_BASE}/turismo/muelle/cierre/anexo.pdf?empresa_id=${empresaId}&fecha=${fecha}`;
    window.open(url, '_blank');
  };

  const descargarInformeVentas = () => {
    const url = `${API_BASE}/turismo/informes/ventas-diarias.pdf?empresa_id=${empresaId}&fecha=${fecha}`;
    window.open(url, '_blank');
  };

  const descargarInformeVentasXlsx = () => {
    // El anexo XLSX es descargable (attachment). Se abre en pestaña nueva y
    // el navegador dispara la descarga sin salir de la pantalla del cierre.
    const url = `${API_BASE}/turismo/informes/ventas-diarias.xlsx?empresa_id=${empresaId}&fecha=${fecha}`;
    window.open(url, '_blank');
  };

  // Cobros del día agrupados por método, para poder desplegar QUÉ reservas
  // sustentan cada cifra. El dato ya viene en el snapshot (`pagos_muelle` de
  // cada reserva); aquí solo se aplana y agrupa — sin consultas nuevas.
  //
  // El agrupado espeja los tres tiles: todo lo que no es efectivo ni
  // transferencia cae en "Tarjeta", igual que la suma que muestra el tile.
  const cobrosPorMetodo = useMemo(() => {
    const g = { Efectivo: [], Tarjeta: [], Transferencia: [] };
    for (const sal of (data?.salidas || [])) {
      for (const r of (sal.reservas || [])) {
        for (const p of (r.pagos_muelle || [])) {
          const m = (p.metodo || '').toLowerCase();
          const grupo = m === 'efectivo' ? 'Efectivo'
                      : m === 'transferencia' ? 'Transferencia'
                      : 'Tarjeta';
          // La referencia del voucher es el dato con el que se cotejan los
          // datáfonos; viene embebida en las notas.
          const ref = (p.notas || '').split('ref:')[1]?.trim() || null;
          g[grupo].push({
            id: p.id, monto: Number(p.monto || 0), hora: p.hora, ref,
            codigo: r.codigo, titular: r.titular, voucher: p.voucher || null,
          });
        }
      }
    }
    // Por hora: el cajero revisa los comprobantes en el orden en que los recibió.
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.hora || '').localeCompare(b.hora || ''));
    return g;
  }, [data]);

  if (isLoading || !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }

  const totales = data.totales_dia;
  const caja = totales.caja_fisica;

  const ingresosCuentas = totales.ingresos_por_cuenta || { subtotal: 0, num_cuentas: 0, items: [] };
  const canales = totales.canales_venta;
  const cobrar = totales.por_cobrar_agencias;
  const revs = totales.reversiones;
  const gastos = totales.gastos || { subtotal: 0, items: [] };
  const efectivoNeto = totales.efectivo_neto ?? caja.efectivo;
  const eficiencia = data.eficiencia_registro || null;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh',
                overflowX: 'hidden' /* atrapa cualquier desbordamiento residual */ }}>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap' /* permite que título y botones se acomoden
                                       en filas propias si no caben juntos */ }}>
        {/* flex-basis mínimo de 280px al bloque del título: si no cabe con los
            botones, se salta a una línea propia con anchura completa en vez de
            comprimirse hasta 1 palabra por renglón. */}
        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 800, color: '#0f172a' }}>
            Cierre de Muelle
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            Cuadre de caja · 3 lados · reversiones · cuadre físico · firmas.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    flexWrap: 'wrap' }}>
          <TextField type="date" size="small" value={fecha} onChange={e => setFecha(e.target.value)}
            InputProps={{ startAdornment: <CalendarTodayIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 0.5 }} /> }}
            sx={{ flex: { xs: '1 1 140px', sm: 'none' }, minWidth: { xs: 140, sm: 150 } }} />
          <IconButton onClick={refetch} disabled={isFetching} size="small">
            <RefreshIcon sx={{ ...(isFetching && { animation: 'spin 1s linear infinite' }) }} />
          </IconButton>
          {/* Atajo a auditoría del día activo (mismo destino que el del muelle) */}
          <Tooltip title="Auditoría del día (todo lo que pasó en muelle)">
            <IconButton size="small" component="a"
              href={`/trazabilidad?fecha_desde=${fecha}&fecha_hasta=${fecha}`}
              target="_blank" rel="noopener noreferrer">
              <HistoryIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={descargarPDF}
            sx={{ textTransform: 'none' }}>PDF</Button>
          <Tooltip title="Anexo detallado en PDF (una sección por cliente, con resumen del día)">
            <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={descargarAnexoPDF}
              sx={{ textTransform: 'none' }}>Anexo PDF</Button>
          </Tooltip>
          <Tooltip title="Anexo detallado: una fila por movimiento (para Excel / conciliación bancaria)">
            <Button variant="outlined" size="small" startIcon={<TableChartIcon />} onClick={descargarAnexoCSV}
              sx={{ textTransform: 'none' }}>Anexo CSV</Button>
          </Tooltip>
          <Tooltip title="Informe Diario de Ventas: resumen de embarque, recaudo y análisis financiero">
            <Button variant="contained" size="small" startIcon={<PictureAsPdfIcon />} onClick={descargarInformeVentas}
              sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #2b6cb0 0%, #1e40af 100%)',
                    boxShadow: '0 4px 12px rgba(43, 108, 176, 0.4)' }}>
              Informe Ventas
            </Button>
          </Tooltip>
          <Tooltip title="Anexo detallado en Excel (.xlsx) — editable, con filtros y subtotales por pasadía">
            <Button variant="contained" size="small" startIcon={<TableChartIcon />} onClick={descargarInformeVentasXlsx}
              sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.4)' }}>
              Excel
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Banner de estado del cierre */}
      {/* Código del cierre prominente — siempre visible */}
      {(cierre?.codigo || data.codigo_preliminar) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, p: 1.5,
                    borderRadius: 2, bgcolor: '#0f172a', color: '#fff' }}>
          <Chip label={cierre?.codigo || data.codigo_preliminar}
            sx={{ bgcolor: '#fbbf24', color: '#0f172a', fontWeight: 800, fontSize: 14, height: 32 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#cbd5e1', fontWeight: 700, textTransform: 'uppercase' }}>
              Código del cierre
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#fff' }}>
              {cierre?.codigo
                ? <>Estado: <strong>{cierre.estado.toUpperCase()}</strong></>
                : <em style={{ color: '#94a3b8' }}>Preliminar · se confirma al firmar</em>}
            </Typography>
          </Box>
        </Box>
      )}

      {yaAprobado && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ✓ Cierre APROBADO por {cierre.aprobacion_supervisor_nombre} ({new Date(cierre.aprobacion_supervisor_at).toLocaleString('es-CO')})
        </Alert>
      )}
      {yaFirmado && !yaAprobado && (
        <Alert severity="info" sx={{ mb: 2 }}>
          ✓ Cierre FIRMADO por {cierre.firma_cajero_nombre} — esperando Vo.Bo. del supervisor.
        </Alert>
      )}
      {/* Aviso de migración al criterio devengado (opción A). Se muestra en
          cierres firmados ANTES de 2026-07-19: el snapshot inmutable conservó
          las cifras del criterio viejo (fecha del pago) y podría diferir del
          cálculo actual (fecha del servicio). No se re-firman por integridad
          de auditoría — el snapshot original es lo que firmó el cajero. */}
      {yaFirmado && cierre?.firma_cajero_at &&
       new Date(cierre.firma_cajero_at) < new Date('2026-07-19T00:00:00') && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Cierre firmado antes de la migración contable del 19-jul-2026.</strong>{' '}
          Este documento conserva las cifras con las que se firmó (criterio de caja: fecha del pago).
          A partir de esa fecha, el sistema atribuye el recaudo al día del pasadía (criterio devengado).
          El total firmado en este documento es inmutable por auditoría; el cálculo dinámico actual puede diferir.
        </Alert>
      )}

      {/* Layout 2 columnas SOLO desde lg (>=1200px). En portátiles medianos
          (900-1199px) la vista se apila, así los 5 tiles de ingresos y las
          tablas quedan con anchura completa en vez de apretados a la mitad. */}
      <Box sx={{ display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
                  gap: 2 }}>

        {/* COLUMNA IZQUIERDA: datos del día */}
        <Box>
          {/* Encabezado vendedor */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
                Operación del turno
              </Typography>
              <TextField label="Vendedor / Cajero" value={vendedorNombre} disabled={yaFirmado}
                onChange={e => setVendedorNombre(e.target.value)} size="small" fullWidth />
            </CardContent>
          </Card>

          {/* Resumen del día (3 lados) */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', mb: 1.5 }}>Resumen del día</Typography>

              {/* Recaudo físico en muelle HOY — bloque con identidad visual
                  propia. Es lo que el cajero debe cuadrar contra el conteo
                  físico (efectivo + lote datáfono + comprobantes). Está
                  contenido dentro de "Ingresos por cuenta destino", NO se
                  suma aparte. */}
              <Box sx={{
                p: { xs: 1.25, md: 1.5 }, mb: 2, borderRadius: 2,
                border: '1px solid #86efac',
                background: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)',
              }}>
                {/* Header con icono + descripción operativa */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                            flexWrap: 'wrap' }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    color: '#fff', flexShrink: 0,
                  }}>
                    <LocalAtmIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#166534' }}>
                      Recaudo en muelle · HOY
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#15803d' }}>
                      Control de caja del punto de embarque · {caja.num_transacciones}{' '}
                      {caja.num_transacciones === 1 ? 'transacción' : 'transacciones'} cobradas hoy
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#15803d',
                                     fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' }}>
                    {fmt(caja.subtotal)}
                  </Typography>
                </Box>

                {/* 3 tiles por fuente real de cash: efectivo, tarjeta,
                    transferencia. Consolidamos tarjeta_credito + tarjeta_debito
                    + bold_link + otros en un solo pill "Tarjeta" porque en la
                    práctica el operador usa metodo_pago='tarjeta' genérico
                    (los otros buckets quedan siempre en 0). Así los 3 pills
                    suman exactamente el subtotal del header. */}
                <Box sx={{ display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr', sm: 'repeat(3,1fr)',
                            },
                            gap: 1 }}>
                  <Pill k="Efectivo" v={caja.efectivo} c="#16a34a"
                        cobros={cobrosPorMetodo.Efectivo} onVoucher={setVoucherAbierto} />
                  <Pill k="Tarjeta"
                        v={(caja.tarjeta_credito || 0) + (caja.tarjeta_debito || 0)
                           + (caja.bold_link || 0) + (caja.otros || 0)}
                        c="#0ea5e9" cobros={cobrosPorMetodo.Tarjeta} onVoucher={setVoucherAbierto} />
                  <Pill k="Transferencia" v={caja.transferencia} c="#0d9488"
                        cobros={cobrosPorMetodo.Transferencia} onVoucher={setVoucherAbierto} />
                </Box>

                {/* Nota aclaratoria — evita el malentendido de sumar aparte */}
                <Typography sx={{ fontSize: 9.5, color: '#64748b', mt: 1,
                                   fontStyle: 'italic', textAlign: 'right' }}>
                  Incluido dentro de "Ingresos por cuenta destino" — no se suma aparte.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mt: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase' }}>
                  Ingresos por cuenta destino del día
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#0f172a' }}>
                  {fmt(ingresosCuentas.subtotal)}
                  <Box component="span" sx={{ ml: 0.5, color: '#94a3b8', fontWeight: 500, fontSize: 10 }}>
                    ({ingresosCuentas.num_cuentas} {ingresosCuentas.num_cuentas === 1 ? 'cuenta' : 'cuentas'})
                  </Box>
                </Typography>
              </Box>
              {/* Desglose HOY vs ANTICIPOS previos — cuadra con caja física
                  (HOY) y separa lo que entró antes (anticipos). Los operadores
                  ya no confunden "ingresos de hoy" con "pagos aplicados a las
                  salidas de hoy que en realidad entraron días atrás". */}
              {(ingresosCuentas.subtotal_anticipo || 0) > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap',
                            p: 0.75, bgcolor: '#fffbeb', borderRadius: 1,
                            border: '1px dashed #fde68a' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                              flex: '1 1 140px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                                bgcolor: '#16a34a', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 600 }}>
                      HOY (cash del día)
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#15803d',
                                       ml: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                      {fmt(ingresosCuentas.subtotal_hoy || 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                              flex: '1 1 140px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                                bgcolor: '#d97706', flexShrink: 0 }} />
                    <Tooltip title="Pagos hechos días atrás que ya estaban en las cuentas antes de hoy — respaldan las salidas de hoy pero NO son ingreso de hoy">
                      <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 600,
                                         cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>
                        Anticipos previos
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#d97706',
                                       ml: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                      {fmt(ingresosCuentas.subtotal_anticipo || 0)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Desglose neto Entretur vs comisión en custodia — solo aparece
                  si hay comisiones custodia (evita ruido en días sin agencias) */}
              {(ingresosCuentas.subtotal_comision_custodia || 0) > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1.25, flexWrap: 'wrap',
                            p: 0.75, bgcolor: '#f8fafc', borderRadius: 1,
                            border: '1px dashed #cbd5e1' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                              flex: '1 1 140px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 0.5,
                                bgcolor: '#0d9488', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 600 }}>
                      Neto Entretur
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#0d9488',
                                       ml: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                      {fmt(ingresosCuentas.subtotal_neto || 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                              flex: '1 1 140px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 0.5,
                                bgcolor: '#8b5cf6', flexShrink: 0 }} />
                    <Tooltip title="Comisión cobrada por adelantado por Entretur — la agencia se la lleva en el cruce/liquidación">
                      <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 600,
                                         cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>
                        Comisión en custodia
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#8b5cf6',
                                       ml: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                      {fmt(ingresosCuentas.subtotal_comision_custodia || 0)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {ingresosCuentas.items.length === 0 ? (
                <Typography sx={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', mb: 2 }}>
                  Sin ingresos registrados hoy.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, mb: 2 }}>
                  {ingresosCuentas.items.map((c, i) => (
                    <CuentaDestinoItem
                      key={c.cuenta_id ?? `k-${i}`}
                      cuenta={c}
                      empresaId={empresaId}
                      fecha={fecha}
                    />
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              <SeccionTitulo titulo="Por cobrar a agencias (ALIADOS)" valor={cobrar.subtotal} color="#dc2626"
                sub={`${cobrar.num_agencias} agencia${cobrar.num_agencias === 1 ? '' : 's'}`} />
              {cobrar.por_agencia?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                  {cobrar.por_agencia.map(a => (
                    <Box key={a.agencia_id} sx={{ display: 'flex', justifyContent: 'space-between', p: 0.75,
                                                   bgcolor: '#fef2f2', borderRadius: 1, alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>{a.nombre}</Typography>
                        <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                          {a.reservas} reservas · comisión {fmt(a.comision)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#991b1b' }}>{fmt(a.monto)}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 2 }}>Sin pendientes hoy.</Typography>
              )}

              <Divider sx={{ my: 1.5 }} />

              <SeccionTitulo titulo="Reservas por canal de venta"
                valor={canales.internet.valor + canales.aliados.valor + canales.comercial.valor} color="#475569" />
              <Box sx={{ display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' },
                          gap: 1, mb: 1 }}>
                <CanalTile k="Internet" v={canales.internet} c="#0ea5e9" />
                <CanalTile k="Aliados" v={canales.aliados} c="#dc2626" />
                <CanalTile k="Comercial" v={canales.comercial} c="#0d9488" />
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Venta neta del día — visión comercial (no cash) */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                          p: 1.5, bgcolor: '#0f172a', borderRadius: 1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                    Venta neta del día
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: '#64748b' }}>
                    Neto Entretur de todas las ventas · no es cash recaudado
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: '#64748b', mt: 0.25 }}>
                    Recaudado en muelle: {fmt(caja.subtotal)} · CxC agencias: {fmt(cobrar.subtotal)}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{fmt(totales.facturado_neto)}</Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Eficiencia de registro — indicador comercial (Bloque 3 · lag) */}
          {eficiencia && eficiencia.total_evaluables > 0 && (
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                      Lag de registro
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                      Días entre voucher/pago y carga de la reserva — {eficiencia.total_evaluables} de {eficiencia.total_reservas_dia} evaluables
                    </Typography>
                  </Box>
                  <Chip
                    label={`${eficiencia.pct_al_dia}% cargadas oportunamente`}
                    size="small"
                    sx={{
                      bgcolor: eficiencia.pct_al_dia >= 80 ? '#dcfce7' :
                                eficiencia.pct_al_dia >= 50 ? '#fef3c7' : '#fee2e2',
                      color:   eficiencia.pct_al_dia >= 80 ? '#15803d' :
                                eficiencia.pct_al_dia >= 50 ? '#a16207' : '#b91c1c',
                      fontWeight: 800, fontSize: 12
                    }}
                  />
                </Box>

                {/* 4 tiles: verde / amarillo / rojo / justificado */}
                <Box sx={{ display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' },
                            gap: 1, mb: 1.5 }}>
                  {[
                    { label: '🟢 Oportunas',   valor: eficiencia.verde,       sub: 'lag 0-1d',   bg: '#dcfce7', color: '#15803d' },
                    { label: '🟡 Rezagadas',   valor: eficiencia.amarillo,    sub: 'lag 2-3d',   bg: '#fef3c7', color: '#a16207' },
                    { label: '🔴 Retrasadas',  valor: eficiencia.rojo,        sub: 'lag >3d',    bg: '#fee2e2', color: '#b91c1c' },
                    { label: '🟪 Justificadas',valor: eficiencia.justificado, sub: 'con motivo', bg: '#e0e7ff', color: '#3730a3' },
                  ].map(t => (
                    <Box key={t.label} sx={{ p: 1.25, bgcolor: t.bg, borderRadius: 1.5, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: t.color, lineHeight: 1 }}>
                        {t.valor}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: t.color, fontWeight: 700, mt: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        {t.label}
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: t.color, opacity: 0.7 }}>{t.sub}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Lag agregado */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', p: 1.25,
                            bgcolor: '#f8fafc', borderRadius: 1.5, mb: 1.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                      Lag mediano
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                      {eficiencia.lag_mediano_dias}d
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                      Lag promedio
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                      {eficiencia.lag_promedio_dias}d
                    </Typography>
                  </Box>
                </Box>

                {/* Top comerciales con mayor lag */}
                {eficiencia.top_comerciales_rezago?.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700,
                                       textTransform: 'uppercase', mb: 0.75 }}>
                      Comerciales con más rezago
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {eficiencia.top_comerciales_rezago.map((c, i) => (
                        <Box key={c.nombre + i}
                             sx={{ display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    gap: { xs: 0.5, sm: 1 },
                                    p: 0.75, bgcolor: '#fff',
                                    border: '1px solid #e2e8f0', borderRadius: 1 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a',
                                             wordBreak: 'break-word' }}>
                            {i + 1}. {c.nombre}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center',
                                      flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                              {c.reservas} {c.reservas === 1 ? 'reserva' : 'reservas'}
                              {c.rojas > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}> · {c.rojas}🔴</span>}
                              {c.justificadas > 0 && <span style={{ color: '#3730a3' }}> · {c.justificadas}🟪</span>}
                            </Typography>
                            <Chip label={`Lag ${c.lag_mediano}d`} size="small"
                              sx={{ height: 20, fontSize: 11, fontWeight: 700,
                                    bgcolor: c.lag_mediano <= 1 ? '#dcfce7' :
                                              c.lag_mediano <= 3 ? '#fef3c7' : '#fee2e2',
                                    color:   c.lag_mediano <= 1 ? '#15803d' :
                                              c.lag_mediano <= 3 ? '#a16207' : '#b91c1c' }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Reversiones */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Reversiones</Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    Anulaciones / devoluciones del día — Total: {fmt(revs.subtotal)}
                  </Typography>
                </Box>
                {!yaFirmado && (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => setRevOpen(true)} sx={{ textTransform: 'none' }}>
                    Registrar
                  </Button>
                )}
              </Box>
              {revs.items.length === 0 ? (
                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Sin reversiones hoy.</Typography>
              ) : isMobile ? (
                /* Cards en mobile (tabla no cabe bien) */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {revs.items.map(r => (
                    <Box key={r.id} sx={{ p: 1, borderRadius: 1, border: '1px solid #e2e8f0', bgcolor: '#fafbfc' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }} noWrap>
                            {r.cliente_nombre || '—'}
                          </Typography>
                          {r.codigo_reserva && (
                            <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>{r.codigo_reserva}</Typography>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#dc2626' }}>{fmt(r.valor)}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 11, color: '#475569', mt: 0.25 }}>{r.motivo}</Typography>
                      {r.metodo_devolucion && (
                        <Typography sx={{ fontSize: 10, color: '#64748b' }}>via {r.metodo_devolucion}</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: 10, fontWeight: 700 }}>Cliente</TableCell>
                      <TableCell sx={{ fontSize: 10, fontWeight: 700 }}>Motivo</TableCell>
                      <TableCell sx={{ fontSize: 10, fontWeight: 700 }} align="right">Valor</TableCell>
                      <TableCell sx={{ fontSize: 10, fontWeight: 700 }}>Método</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {revs.items.map(r => (
                      <TableRow key={r.id}>
                        <TableCell sx={{ fontSize: 11 }}>
                          {r.cliente_nombre || '—'}
                          {r.codigo_reserva && <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>{r.codigo_reserva}</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{r.motivo}</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }} align="right">{fmt(r.valor)}</TableCell>
                        <TableCell sx={{ fontSize: 10 }}>{r.metodo_devolucion || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Gastos del día */}
          <GastosSection
            empresaId={empresaId} fecha={fecha} gastos={gastos}
            yaFirmado={yaFirmado} isMobile={isMobile}
            onChanged={refetch}
            toast={toast}
          />

          {/* Detalle por salida */}
          {data.salidas.map(s => <SalidaAccordion key={s.salida_id} salida={s} />)}
        </Box>

        {/* COLUMNA DERECHA: cuadre físico vs sistema */}
        <Box>
          <Card sx={{ borderRadius: 2, position: { lg: 'sticky' }, top: 16 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', mb: 1.5 }}>
                Cuadre físico vs sistema
              </Typography>

              {/* Denominaciones */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', mb: 0.5, textTransform: 'uppercase' }}>
                Conteo de billetes
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                {DENOMINACIONES.map(d => {
                  const cant = Number(denoms[d] || 0);
                  const sub = cant * d;
                  return (
                    <Box key={d} sx={{ display: 'grid',
                      gridTemplateColumns: { xs: '60px 1fr 80px', sm: '70px 1fr 90px' },
                      gap: 0.5, alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textAlign: 'right' }}>{fmt(d)}</Typography>
                      <TextField type="number" size="small" disabled={yaFirmado}
                        value={denoms[d] || ''}
                        onChange={e => setDenoms({ ...denoms, [d]: e.target.value })}
                        inputProps={{ min: 0, inputMode: 'numeric', pattern: '[0-9]*',
                          style: { fontSize: 14, textAlign: 'center', padding: '8px 4px' } }} />
                      <Typography sx={{ fontSize: 11, color: '#64748b', textAlign: 'right' }}>
                        {sub > 0 ? fmt(sub) : '—'}
                      </Typography>
                    </Box>
                  );
                })}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f0fdf4', borderRadius: 1, mt: 0.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#15803d' }}>Total efectivo físico</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#15803d' }}>{fmt(efectivoFromDenoms)}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Otros físicos (tarjetas, bold, transf) */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', mb: 0.5, textTransform: 'uppercase' }}>
                Otros recaudos físicos
              </Typography>
              {[
                { k: 'tarjetas', label: 'Tarjetas (lote datáfono)' },
                { k: 'bold', label: 'BOLD' },
                { k: 'transferencia', label: 'Transferencias' },
                { k: 'otros', label: 'Otros' },
              ].map(o => (
                <Box key={o.k} sx={{ display: 'grid',
                    gridTemplateColumns: { xs: '1fr 90px', sm: '1fr 110px' },
                    gap: 0.5, mb: 0.5, alignItems: 'center' }}>
                  <Typography sx={{ fontSize: 11, color: '#475569' }}>{o.label}</Typography>
                  <TextField type="number" size="small" disabled={yaFirmado}
                    value={fisico[o.k] || ''}
                    onChange={e => setFisico({ ...fisico, [o.k]: e.target.value })}
                    inputProps={{ min: 0, inputMode: 'numeric',
                      style: { fontSize: 13, textAlign: 'right', padding: '8px 6px' } }} />
                </Box>
              ))}

              <Divider sx={{ my: 1.5 }} />

              {/* Comparación */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Comp k="Sistema bruto" v={sistemaTotal} c="#475569" />
                {gastosTotal > 0 && <Comp k="(−) Gastos del día" v={-gastosTotal} c="#dc2626" />}
                <Comp k="Sistema NETO" v={sistemaNeto} c="#475569" bold />
                <Comp k="Físico (cajero)" v={fisicoTotal} c="#0f172a" bold />
                <Box sx={{ p: 1, bgcolor: cuadraExacto ? '#dcfce7' : (diferencia > 0 ? '#fef3c7' : '#fee2e2'),
                            borderRadius: 1, mt: 0.5 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                     color: cuadraExacto ? '#15803d' : (diferencia > 0 ? '#92400e' : '#991b1b') }}>
                    {cuadraExacto ? '✓ Cuadre exacto' : (diferencia > 0 ? '⚠ Sobrante' : '⛔ Faltante')}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800,
                                     color: cuadraExacto ? '#15803d' : (diferencia > 0 ? '#92400e' : '#991b1b') }}>
                    {diferencia >= 0 ? '+' : ''}{fmt(diferencia)}
                  </Typography>
                  {!cuadraExacto && (
                    <Typography sx={{ fontSize: 10, color: '#991b1b', mt: 0.5 }}>
                      Debes cuadrar exacto antes de firmar.
                    </Typography>
                  )}
                </Box>
              </Box>

              <TextField label="Notas" multiline minRows={2} maxRows={4} size="small"
                value={notas} onChange={e => setNotas(e.target.value)} disabled={yaFirmado}
                sx={{ mt: 1.5, width: '100%' }} />

              {/* Primera puerta: la recepción. Se muestra apenas se abre la
                  pantalla, no cuando falla un intento de firmar. */}
              {!recepcionLista && !yaFirmado && (
                <Alert severity="warning" sx={{ mt: 1.5, py: 0.75 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>
                    Recepción sin cerrar · {pendientesRecepcion.length} pasajero(s)
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, mb: 0.75 }}>
                    Cada uno debe quedar con check-in o marcado como no-show
                    antes de poder firmar el cierre.
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 11.5 }}>
                    {pendientesRecepcion.slice(0, 8).map(p => (
                      <li key={p.reservacion_id}>
                        <b>{p.codigo_reserva}</b> — {p.cliente}
                        {p.saldo_pendiente > 0 && ` · debe ${fmt(p.saldo_pendiente)}`}
                      </li>
                    ))}
                  </Box>
                  {pendientesRecepcion.length > 8 && (
                    <Typography sx={{ fontSize: 11, mt: 0.5 }}>
                      y {pendientesRecepcion.length - 8} más.
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Por qué está bloqueada la firma. Un botón gris sin explicación
                  se lee como "no tengo permiso"; esto dice qué falta y dónde. */}
              {bloqueo && (
                <Alert severity="warning" sx={{ mt: 1.5, fontSize: 12, py: 0.75 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>
                    {bloqueo.code === 'RECEPCION_INCOMPLETA'
                      ? 'Falta cerrar la recepción'
                      : bloqueo.code === 'DESCUADRE_POR_METODO'
                        ? 'El total cuadra, pero las casillas no'
                        : 'No se puede firmar'}
                  </Typography>

                  {bloqueo.code === 'RECEPCION_INCOMPLETA' && (
                    <>
                      <Typography sx={{ fontSize: 11.5, mb: 0.75 }}>
                        Cada pasajero debe quedar con check-in o marcado como
                        no-show antes de firmar.
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 11.5 }}>
                        {(bloqueo.pendientes || []).slice(0, 8).map(p => (
                          <li key={p.reservacion_id}>
                            <b>{p.codigo_reserva}</b> — {p.cliente}
                            {p.saldo_pendiente > 0 && ` · debe ${fmt(p.saldo_pendiente)}`}
                          </li>
                        ))}
                      </Box>
                      {(bloqueo.pendientes || []).length > 8 && (
                        <Typography sx={{ fontSize: 11, mt: 0.5 }}>
                          y {bloqueo.pendientes.length - 8} más.
                        </Typography>
                      )}
                    </>
                  )}

                  {bloqueo.code === 'DESCUADRE_POR_METODO' && (
                    <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 11.5 }}>
                      {(bloqueo.casillas || []).map(c => (
                        <li key={c.metodo}>
                          <b>{c.metodo}</b>: declaras {fmt(c.declarado)} y el sistema
                          tiene {fmt(c.sistema)}{' '}
                          <b>({c.diferencia > 0 ? '+' : ''}{fmt(c.diferencia)})</b>
                        </li>
                      ))}
                    </Box>
                  )}

                  {!['RECEPCION_INCOMPLETA', 'DESCUADRE_POR_METODO'].includes(bloqueo.code) && (
                    <Typography sx={{ fontSize: 11.5 }}>{bloqueo.message}</Typography>
                  )}
                </Alert>
              )}

              {/* Acciones */}
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {!yaFirmado && (
                  <>
                    <Button fullWidth variant="outlined" startIcon={<SaveIcon />}
                      onClick={() => guardar(false)} disabled={saving}
                      sx={{ textTransform: 'none' }}>
                      Guardar borrador
                    </Button>
                    <Tooltip title={
                      !recepcionLista
                        ? `Faltan ${pendientesRecepcion.length} pasajero(s) por resolver en recepción`
                        : (!cuadraExacto ? 'No se puede firmar con descuadre — debe ser exacto' : '')
                    }>
                      <span>
                        <Button fullWidth variant="contained" startIcon={<CheckCircleIcon />}
                          onClick={() => guardar(true)}
                          disabled={saving || !vendedorNombre.trim() || !cuadraExacto || !recepcionLista}
                          sx={{ textTransform: 'none', fontWeight: 700,
                                 background: (cuadraExacto && recepcionLista)
                                   ? 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)'
                                   : '#cbd5e1' }}>
                          Firmar como cajero
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
                {yaFirmado && !yaAprobado && (
                  ['admin', 'superadmin'].includes(getUserRole()) ? (
                    <Button fullWidth variant="contained" startIcon={<GavelIcon />}
                      onClick={() => setAprobarOpen(true)}
                      sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#1e3a8a' }}>
                      Vo.Bo. Supervisor
                    </Button>
                  ) : (
                    <Alert severity="info" sx={{ fontSize: 11, py: 0.5 }}>
                      Esperando Vo.Bo. del supervisor (admin).
                    </Alert>
                  )
                )}
                {yaAprobado && (
                  <Alert severity="success" sx={{ fontSize: 11, py: 0.5 }}>
                    Cierre aprobado e inmutable.
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Diálogos */}
      <ReversionDialog open={revOpen} onClose={() => setRevOpen(false)} empresaId={empresaId}
        fecha={fecha} fullScreen={isMobile}
        onSaved={() => { setRevOpen(false); refetch(); toast.success('Reversión registrada'); }}
        onError={msg => toast.error(msg)} />
      {/* Visor del soporte a tamaño completo */}
      <Dialog open={!!voucherAbierto} onClose={() => setVoucherAbierto(null)}
              maxWidth="md" fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
                {voucherAbierto?.codigo}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                {voucherAbierto?.titular}
                {voucherAbierto?.ref && ` · ref ${voucherAbierto.ref}`}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0d9488',
                               fontVariantNumeric: 'tabular-nums' }}>
              {voucherAbierto ? fmt(voucherAbierto.monto) : ''}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: '#0f172a' }}>
          {voucherAbierto?.voucher?.imagen_url && (
            <Box component="img"
                 src={`${API_BASE}${voucherAbierto.voucher.imagen_url}`}
                 alt={`Soporte de ${voucherAbierto.codigo}`}
                 sx={{ display: 'block', maxWidth: '100%', maxHeight: '75vh',
                       margin: '0 auto', borderRadius: 1 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open(
            `${API_BASE}${voucherAbierto?.voucher?.imagen_url}`, '_blank')}
            sx={{ textTransform: 'none' }}>
            Abrir en pestaña nueva
          </Button>
          <Button variant="contained" onClick={() => setVoucherAbierto(null)}
                  sx={{ textTransform: 'none' }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <AprobarDialog open={aprobarOpen} onClose={() => setAprobarOpen(false)} onConfirm={aprobar}
        fullScreen={isMobile} />

      <ToastContainer />
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}

function SeccionTitulo({ titulo, valor, sub, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{titulo}</Typography>
        {sub && <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>{sub}</Typography>}
      </Box>
      <Typography sx={{ fontSize: 17, fontWeight: 800, color }}>{fmt(valor)}</Typography>
    </Box>
  );
}

/**
 * Pill — cifra por método de cobro, desplegable.
 *
 * Al abrirlo muestra las reservas que sustentan esa cifra: código, titular,
 * hora, referencia del voucher y monto. Es lo que el cajero necesita para
 * cotejar uno por uno contra los comprobantes que tiene en la mano — antes
 * solo veía un número redondo y, si no le cuadraba, no tenía dónde mirar.
 *
 * Colapsado por defecto: nueve cobros caben, pero un sábado son treinta y la
 * tarjeta se volvería una pared de texto.
 */
function Pill({ k, v, c, cobros = [], onVoucher }) {
  const [abierto, setAbierto] = useState(false);
  const has = Number(v) > 0;
  const desplegable = cobros.length > 0;

  const horaCorta = (iso) => {
    if (!iso) return '';
    return _horaBogota(iso);
  };

  return (
    <Box sx={{ borderRadius: 1.5, bgcolor: has ? `${c}15` : '#f8fafc',
                border: '1px solid', borderColor: has ? `${c}40` : '#e2e8f0',
                overflow: 'hidden', alignSelf: 'start',
                // Abierto ocupa la fila completa: en tres columnas el código
                // de reserva —justo el dato con el que se cotejan los
                // comprobantes— salía cortado a "ENT-2026082…".
                gridColumn: abierto ? '1 / -1' : 'auto' }}>
      <Box
        onClick={desplegable ? () => setAbierto(o => !o) : undefined}
        sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.75,
              cursor: desplegable ? 'pointer' : 'default',
              '&:hover': desplegable ? { bgcolor: `${c}22` } : {} }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                            color: has ? c : '#94a3b8' }}>
            {k}
            {desplegable && (
              <Box component="span" sx={{ fontWeight: 600, opacity: 0.75 }}>
                {' · '}{cobros.length} {cobros.length === 1 ? 'cobro' : 'cobros'}
              </Box>
            )}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: has ? c : '#cbd5e1',
                            fontVariantNumeric: 'tabular-nums' }}>
            {fmt(v)}
          </Typography>
        </Box>
        {desplegable && (
          <ExpandMoreIcon sx={{ fontSize: 16, color: c, flexShrink: 0,
                                transition: 'transform .15s ease',
                                transform: abierto ? 'rotate(180deg)' : 'none' }} />
        )}
      </Box>

      {/* Una sola columna siempre. Con dos, cada una quedaba en ~280px y la
          hora y el monto de la derecha se salían del tile y quedaban cortados
          — se veía el código y la referencia, pero no la plata, que es justo
          lo que el cajero está cotejando. Seis filas de alto no molestan;
          una cifra invisible sí. */}
      {desplegable && abierto && (
        <Box sx={{ borderTop: `1px solid ${c}30`, bgcolor: '#fff', px: 1, py: 0.5 }}>
          {cobros.map(p => (
            <Box key={p.id} sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5,
              borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' },
            }}>
              {/* Miniatura del soporte. Es lo que el cajero coteja contra el
                  comprobante físico; verla aquí le evita ir a otra pantalla. */}
              {p.voucher?.imagen_url ? (
                <Box
                  component="img"
                  // Miniatura generada por el servidor, no el original: los
                  // soportes subidos antes del tope de 1600px pesan hasta
                  // 4 MB y aquí se pintan en 34px. El visor sí abre el
                  // original a tamaño completo.
                  src={`${API_BASE}/turismo/soportes/thumb/${p.voucher.imagen_url.split('/').pop()}?w=96`}
                  loading="lazy"
                  alt={`Soporte ${p.codigo}`}
                  onClick={(e) => { e.stopPropagation(); onVoucher?.(p); }}
                  sx={{
                    width: 34, height: 34, flexShrink: 0, borderRadius: 1,
                    objectFit: 'cover', border: '1px solid #e2e8f0',
                    cursor: 'zoom-in', bgcolor: '#f8fafc',
                    '&:hover': { borderColor: c, transform: 'scale(1.06)' },
                    transition: 'transform .12s ease, border-color .12s ease',
                  }}
                />
              ) : (
                <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: 1,
                            border: '1px dashed #e2e8f0', display: 'flex',
                            alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: 8, color: '#cbd5e1', textAlign: 'center',
                                     lineHeight: 1.1 }}>sin<br/>foto</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#0f172a',
                                  overflow: 'hidden', textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap' }}>
                  {p.codigo}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#64748b',
                                  overflow: 'hidden', textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap' }}>
                  {p.titular}
                  {p.ref && <Box component="span" sx={{ color: '#94a3b8' }}> · ref {p.ref}</Box>}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 9.5, color: '#94a3b8', flexShrink: 0 }}>
                {horaCorta(p.hora)}
              </Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: c, flexShrink: 0,
                                fontVariantNumeric: 'tabular-nums' }}>
                {fmt(p.monto)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function CanalTile({ k, v, c }) {
  return (
    <Box sx={{ p: 1, borderRadius: 1.5, border: `1px solid ${c}40`, bgcolor: `${c}15` }}>
      <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: c }}>{k}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: c }}>{fmt(v.valor)}</Typography>
      <Typography sx={{ fontSize: 9, color: '#64748b' }}>{v.reservas} reservas</Typography>
    </Box>
  );
}

function Comp({ k, v, c, bold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography sx={{ fontSize: 11, color: c }}>{k}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: bold ? 800 : 600, color: c }}>{fmt(v)}</Typography>
    </Box>
  );
}

function SalidaAccordion({ salida }) {
  const sub = salida.subtotales;
  return (
    <Accordion defaultExpanded={false} sx={{ mt: 1, borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
            {salida.hora_salida ? salida.hora_salida.slice(0, 5) : '—'} · {salida.paquete}
          </Typography>
          <Chip size="small" label={`${salida.stats.reservas}r · ${salida.stats.pax}p · ${salida.stats.embarcados}e`}
            sx={{ height: 20, fontSize: 10 }} />
          <Box sx={{ flex: 1, textAlign: 'right' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{fmt(sub.facturado)}</Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {salida.reservas.map(r => {
          const tInfo = TIPO_VENTA_LABEL[r.tipo_venta] || TIPO_VENTA_LABEL.directa;
          return (
            <Box key={r.id} sx={{ p: 1, borderRadius: 1, border: '1px solid #e2e8f0', mb: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, minWidth: 0,
                                   wordBreak: 'break-word' }}>{r.codigo} · {r.titular}</Typography>
                <Chip size="small" label={tInfo.label}
                  sx={{ bgcolor: tInfo.bg, color: tInfo.color, height: 18, fontSize: 9 }} />
              </Box>
              <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                {fmtPax(r)} · {fmt(r.precio_total)} · cobrado {fmt(r.cobrado_muelle)}
                {r.por_cobrar_agencia > 0 && ` · por cobrar ${fmt(r.por_cobrar_agencia)}`}
              </Typography>
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}

// Diálogo Reversión
function ReversionDialog({ open, onClose, empresaId, fecha, onSaved, onError, fullScreen }) {
  const [codigo, setCodigo] = useState('');
  const [cliente, setCliente] = useState('');
  const [pax, setPax] = useState('');
  const [motivo, setMotivo] = useState('');
  const [valor, setValor] = useState('');
  const [metodo, setMetodo] = useState('efectivo');
  const [voucher, setVoucher] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setCodigo(''); setCliente(''); setPax(''); setMotivo(''); setValor(''); setMetodo('efectivo'); setVoucher(''); }
  }, [open]);

  const guardar = async () => {
    if (!motivo.trim() || !valor) { onError('Motivo y valor son requeridos'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/muelle/cierre/reversion?empresa_id=${empresaId}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          fecha, codigo_reserva: codigo.trim() || null, cliente_nombre: cliente.trim() || null,
          pax: pax ? parseInt(pax) : null, motivo: motivo.trim(), valor: Number(valor),
          metodo_devolucion: metodo, voucher_referencia: voucher.trim() || null,
          registrado_por_user_id: getUserId(), registrado_por_nombre: getUserName(),
        }),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      onSaved();
    } catch (e) { onError(e.message); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Registrar reversión</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField size="small" label="Código reserva (opcional)" value={codigo} onChange={e => setCodigo(e.target.value)} />
          <TextField size="small" label="No. Pax" type="number" value={pax} onChange={e => setPax(e.target.value)} />
        </Box>
        <TextField size="small" label="Cliente" value={cliente} onChange={e => setCliente(e.target.value)} />
        <TextField size="small" label="Voucher / referencia (opcional)" value={voucher} onChange={e => setVoucher(e.target.value)} />
        <TextField size="small" label="Motivo *" multiline minRows={2} value={motivo} onChange={e => setMotivo(e.target.value)} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField size="small" label="Valor *" type="number" value={valor} onChange={e => setValor(e.target.value)} />
          <TextField size="small" select label="Método devolución" value={metodo} onChange={e => setMetodo(e.target.value)}>
            <MenuItem value="efectivo">Efectivo</MenuItem>
            <MenuItem value="transferencia">Transferencia</MenuItem>
            <MenuItem value="tarjeta">Reverso tarjeta</MenuItem>
            <MenuItem value="bold">Reverso BOLD</MenuItem>
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={saving}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}

// Sección de Gastos del día
function GastosSection({ empresaId, fecha, gastos, yaFirmado, isMobile, onChanged, toast }) {
  const [open, setOpen] = useState(false);
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/muelle/gastos/${id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success('Gasto eliminado');
      onChanged();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
              Gastos del día
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
              Hielo, refrescos, comida, transporte, etc. Se restan del efectivo cobrado.
              Total: <strong style={{ color: '#dc2626' }}>{fmt(gastos.subtotal)}</strong>
            </Typography>
          </Box>
          {!yaFirmado && (
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
              onClick={() => setOpen(true)} sx={{ textTransform: 'none' }}>
              Registrar
            </Button>
          )}
        </Box>
        {gastos.items.length === 0 ? (
          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Sin gastos registrados.</Typography>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {gastos.items.map(g => (
              <Box key={g.id} sx={{ p: 1, borderRadius: 1, border: '1px solid #e2e8f0', bgcolor: '#fafbfc' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Chip label={g.categoria.toUpperCase()} size="small" sx={{ height: 16, fontSize: 9, mb: 0.25 }} />
                    <Typography sx={{ fontSize: 12, color: '#0f172a' }} noWrap>{g.descripcion}</Typography>
                    <a href={g.comprobante_url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#0d9488' }}>
                      📎 Ver comprobante
                    </a>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#dc2626' }}>{fmt(g.monto)}</Typography>
                    {!yaFirmado && (
                      <IconButton size="small" onClick={() => handleDelete(g.id)} sx={{ p: 0, mt: 0.25 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 10, fontWeight: 700 }}>Categoría</TableCell>
                <TableCell sx={{ fontSize: 10, fontWeight: 700 }}>Descripción</TableCell>
                <TableCell sx={{ fontSize: 10, fontWeight: 700 }} align="right">Monto</TableCell>
                <TableCell sx={{ fontSize: 10, fontWeight: 700 }}>Comprobante</TableCell>
                <TableCell sx={{ fontSize: 10, fontWeight: 700 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gastos.items.map(g => (
                <TableRow key={g.id}>
                  <TableCell sx={{ fontSize: 11 }}>
                    <Chip label={g.categoria} size="small" sx={{ height: 18, fontSize: 10 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>{g.descripcion}</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }} align="right">{fmt(g.monto)}</TableCell>
                  <TableCell>
                    <a href={g.comprobante_url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#0d9488' }}>📎 Ver</a>
                  </TableCell>
                  <TableCell>
                    {!yaFirmado && (
                      <IconButton size="small" onClick={() => handleDelete(g.id)}>
                        <DeleteOutlineIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <GastoDialog open={open} onClose={() => setOpen(false)} empresaId={empresaId}
        fecha={fecha} fullScreen={isMobile}
        onSaved={() => { setOpen(false); onChanged(); toast.success('Gasto registrado'); }}
        onError={msg => toast.error(msg)} />
    </Card>
  );
}

// Diálogo Registrar Gasto
function GastoDialog({ open, onClose, empresaId, fecha, onSaved, onError, fullScreen }) {
  const [categoria, setCategoria] = useState('hielo');
  const [descripcion, setDescripcion] = useState('');
  const [monto, setMonto] = useState('');
  const [comprobanteUrl, setComprobanteUrl] = useState('');
  const [comprobanteNombre, setComprobanteNombre] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCategoria('hielo'); setDescripcion(''); setMonto('');
      setComprobanteUrl(''); setComprobanteNombre('');
    }
  }, [open]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData(); fd.append('archivo', file);
      const t = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, { method: 'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, body: fd });
      if (!r.ok) throw new Error('Error subiendo');
      const d = await r.json();
      setComprobanteUrl(d.url); setComprobanteNombre(file.name);
    } catch (err) { onError(err.message); }
    setSubiendo(false);
  };

  const guardar = async () => {
    if (!descripcion.trim() || !monto || !comprobanteUrl) {
      onError('Descripción, monto y comprobante son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/muelle/gastos?empresa_id=${empresaId}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          fecha, categoria, descripcion: descripcion.trim(),
          monto: Number(monto), comprobante_url: comprobanteUrl,
          registrado_por_user_id: getUserId(), registrado_por_nombre: getUserName(),
        }),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      onSaved();
    } catch (e) { onError(e.message); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Registrar gasto del día</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Alert severity="info" sx={{ fontSize: 11 }}>
          El comprobante (foto del recibo) es <strong>obligatorio</strong>. Se paga con el efectivo del muelle.
        </Alert>
        <TextField size="small" select label="Categoría *" value={categoria} onChange={e => setCategoria(e.target.value)}>
          <MenuItem value="hielo">🧊 Hielo</MenuItem>
          <MenuItem value="refrescos">🥤 Refrescos</MenuItem>
          <MenuItem value="comida">🍱 Comida</MenuItem>
          <MenuItem value="transporte">🚐 Transporte</MenuItem>
          <MenuItem value="combustible">⛽ Combustible</MenuItem>
          <MenuItem value="otros">📦 Otros</MenuItem>
        </TextField>
        <TextField size="small" label="Descripción *" multiline minRows={2} value={descripcion}
          onChange={e => setDescripcion(e.target.value)} placeholder="Ej: Hielo en cubos para neveras de las 2 lanchas" />
        <TextField size="small" label="Monto *" type="number" value={monto}
          onChange={e => setMonto(e.target.value)} inputProps={{ min: 0, inputMode: 'numeric' }} />
        <Box sx={{ p: 1.5, border: '1px dashed #cbd5e1', borderRadius: 2 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', mb: 0.5 }}>
            Comprobante *
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button component="label" variant="outlined" size="small" disabled={subiendo}
              startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none' }}>
              {subiendo ? 'Subiendo…' : 'Subir foto'}
              <input hidden type="file" accept=".pdf,image/*" onChange={handleUpload} />
            </Button>
            {comprobanteUrl && (
              <Typography sx={{ fontSize: 11, color: '#16a34a' }} noWrap>✓ {comprobanteNombre}</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={guardar}
          disabled={saving || !descripcion.trim() || !monto || !comprobanteUrl}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Diálogo Aprobación supervisor
function AprobarDialog({ open, onClose, onConfirm, fullScreen }) {
  const [nombre, setNombre] = useState('');
  const [notas, setNotas] = useState('');
  useEffect(() => { if (open) { setNombre(''); setNotas(''); } }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Vo.Bo. del supervisor</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Alert severity="warning" sx={{ fontSize: 11 }}>
          Al aprobar, el cierre queda inmutable. No se podrán hacer cambios.
        </Alert>
        <TextField size="small" label="Tu nombre (supervisor) *" value={nombre} onChange={e => setNombre(e.target.value)} />
        <TextField size="small" label="Notas (opcional)" multiline minRows={2} value={notas} onChange={e => setNotas(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={() => onConfirm(nombre, notas)} disabled={!nombre.trim()}>Aprobar</Button>
      </DialogActions>
    </Dialog>
  );
}


// ============================================================================
// Detalle expandible de una cuenta destino (B2 · desglose click-to-see)
// ============================================================================

// El backend guarda los timestamps con datetime.utcnow() y los serializa con
// .isoformat(), o sea sin sufijo de zona: "2026-08-23T12:55:00". Eso es UTC,
// pero al no decirlo, `new Date()` lo interpreta como hora local del navegador
// y el resultado depende de dónde esté sentado el cajero. Se marca como UTC
// antes de convertir, para que la hora que ve sea siempre la de Bogotá.
function _isoComoUtc(iso) {
  const s = String(iso);
  return /[Zz]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + 'Z';
}

// Convierte una fecha ISO UTC a hora local Bogotá (HH:MM)
function _horaBogota(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(_isoComoUtc(iso));
    return d.toLocaleTimeString('es-CO', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Bogota',
    });
  } catch { return '—'; }
}

/**
 * Item clickeable de la lista "Ingresos por cuenta destino". Al hacer click
 * expande in-place mostrando el detalle de cada movimiento (reserva + vouchers).
 * Lazy fetch: sólo consulta el endpoint cuando el usuario abre.
 */
function CuentaDestinoItem({ cuenta, empresaId, fecha }) {
  const [open, setOpen] = useState(false);
  const [voucherLightbox, setVoucherLightbox] = useState(null);   // { url, id }

  // cuenta_id puede ser null (bucket especial). Backend acepta strings
  // "__efectivo__" y "__sin_cuenta__" — mapeamos por nombre.
  const cuentaKey = cuenta.cuenta_id
    ? String(cuenta.cuenta_id)
    : (cuenta.nombre?.toLowerCase().includes('efectivo') ? '__efectivo__' : '__sin_cuenta__');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['detalle-cuenta', empresaId, fecha, cuentaKey],
    enabled: open,   // ← lazy: solo fetch cuando el user expande
    queryFn: async () => {
      const r = await fetch(
        `${API_BASE}/turismo/muelle/cierre/cuenta/${cuentaKey}?empresa_id=${empresaId}&fecha=${fecha}`,
        { headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 60_000,
  });

  return (
    <>
      <Box sx={{
        borderRadius: 1, bgcolor: '#f0fdfa', border: '1px solid #99f6e4',
        overflow: 'hidden',
      }}>
        {/* Encabezado clickeable */}
        <Box
          onClick={() => setOpen(o => !o)}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 0.25, sm: 1 },
            p: '6px 10px',
            cursor: 'pointer',
            transition: 'background 0.15s',
            '&:hover': { bgcolor: '#ccfbf1' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0,
                      flexWrap: 'wrap', maxWidth: '100%' }}>
            <ExpandMoreIcon sx={{
              fontSize: 16, color: '#0d9488',
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#134e4a',
                               wordBreak: 'break-word' }}>
              {cuenta.nombre}
            </Typography>
            {cuenta.tipo && cuenta.tipo !== '—' && (
              <Typography sx={{
                fontSize: 9, color: '#0d9488', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.4,
                bgcolor: '#ccfbf1', px: 0.75, py: 0.15, borderRadius: 0.5,
              }}>
                {cuenta.tipo}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 10, color: '#64748b' }}>
              {cuenta.num_transacciones} {cuenta.num_transacciones === 1 ? 'tx' : 'txs'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography sx={{
                fontSize: 13, fontWeight: 800, color: '#134e4a',
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {fmt(cuenta.monto)}
              </Typography>
              {(cuenta.monto_comision || 0) > 0 && (
                <Tooltip title={`Neto Entretur ${fmt(cuenta.monto_neto || 0)} + Comisión en custodia ${fmt(cuenta.monto_comision || 0)}`}>
                  <Typography sx={{
                    fontSize: 9, color: '#64748b',
                    fontFamily: 'ui-monospace, monospace', cursor: 'help',
                  }}>
                    <Box component="span" sx={{ color: '#0d9488', fontWeight: 700 }}>
                      {fmt(cuenta.monto_neto || 0)}
                    </Box>
                    {' + '}
                    <Box component="span" sx={{ color: '#8b5cf6', fontWeight: 700 }}>
                      {fmt(cuenta.monto_comision || 0)}
                    </Box>
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        {/* Detalle expandido */}
        {open && (
          <Box sx={{ p: 1, bgcolor: '#fff', borderTop: '1px solid #99f6e4' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : isError ? (
              <Typography sx={{ fontSize: 11, color: '#dc2626', p: 1 }}>
                Error cargando el detalle.
              </Typography>
            ) : data?.movimientos?.length === 0 ? (
              <Typography sx={{ fontSize: 11, color: '#94a3b8', p: 1, fontStyle: 'italic' }}>
                Sin movimientos para esta cuenta en el día.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {(data?.movimientos || []).map(m => (
                  <MovimientoRow
                    key={m.pago_id}
                    m={m}
                    onVerVoucher={setVoucherLightbox}
                  />
                ))}
                {/* Pie con total del detalle — debe cuadrar con el chip */}
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  mt: 0.5, p: 0.75, bgcolor: '#f0fdfa', borderRadius: 1,
                }}>
                  <Typography sx={{ fontSize: 10, color: '#0d9488', fontWeight: 700,
                                     textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Total detalle
                  </Typography>
                  <Typography sx={{
                    fontSize: 12, fontWeight: 800, color: '#134e4a',
                    fontFamily: 'ui-monospace, monospace',
                  }}>
                    {fmt(data?.total_monto || 0)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Lightbox del voucher */}
      <VoucherLightbox
        voucher={voucherLightbox}
        onClose={() => setVoucherLightbox(null)}
      />
    </>
  );
}

/** Una fila de movimiento: reserva + monto + método + vouchers. */
function MovimientoRow({ m, onVerVoucher }) {
  const r = m.reserva || {};
  const hora = _horaBogota(m.hora);
  const primerVoucher = (m.vouchers && m.vouchers[0]) || null;
  const otrosVouchers = (m.vouchers || []).slice(1);

  return (
    <Box sx={{
      p: 0.75, borderRadius: 1, border: '1px solid #e2e8f0', bgcolor: '#fafbfc',
    }}>
      {/* Línea 1: chip HOY/ANTICIPO · hora · código · cliente · monto */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 0.75, flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0, flex: 1 }}>
          {m.es_hoy ? (
            <Chip label="HOY" size="small"
              sx={{ height: 16, fontSize: 9, fontWeight: 800,
                    bgcolor: '#dcfce7', color: '#15803d', flexShrink: 0 }} />
          ) : (
            <Chip label={`ANTIC ${m.dias_anticipo}d`} size="small"
              sx={{ height: 16, fontSize: 9, fontWeight: 800,
                    bgcolor: '#fef3c7', color: '#a16207', flexShrink: 0 }} />
          )}
          <Typography sx={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace', flexShrink: 0 }}>
            {hora}
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0f172a',
                             fontFamily: 'monospace', flexShrink: 0 }}>
            {r.codigo || '—'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#334155', wordBreak: 'break-word' }}>
            {r.cliente || '—'}
          </Typography>
        </Box>
        <Typography sx={{
          fontSize: 12, fontWeight: 800, color: '#134e4a',
          fontFamily: 'ui-monospace, monospace',
        }}>
          {fmt(m.monto)}
        </Typography>
      </Box>

      {/* Línea 2: método · agencia · registrado_por · via_voucher badge */}
      <Box sx={{
        display: 'flex', gap: 0.5, mt: 0.25, alignItems: 'center', flexWrap: 'wrap',
      }}>
        {m.metodo_pago && (
          <Chip label={m.metodo_pago} size="small"
            sx={{ height: 16, fontSize: 9, bgcolor: '#e0f2fe', color: '#075985' }} />
        )}
        {r.agencia_nombre && (
          <Typography sx={{ fontSize: 10, color: '#64748b' }}>
            {r.agencia_nombre}
          </Typography>
        )}
        {m.registrado_por && (
          <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
            · {m.registrado_por}
          </Typography>
        )}
        {m.via_voucher_fallback && (
          <Tooltip title="Cuenta inferida a partir del voucher (el pago no la tenía asignada explícitamente)">
            <Chip label="cuenta inferida" size="small"
              sx={{ height: 16, fontSize: 9, bgcolor: '#fef3c7', color: '#a16207' }} />
          </Tooltip>
        )}
      </Box>

      {/* Línea 3: vouchers */}
      {primerVoucher && (
        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Tooltip title={
            `Voucher #${primerVoucher.id} · ${primerVoucher.tipo || 'sin tipo'}` +
            (primerVoucher.monto_voucher ? ` · $${new Intl.NumberFormat('es-CO').format(primerVoucher.monto_voucher)}` : '') +
            (primerVoucher.referencia_voucher ? ` · ref ${primerVoucher.referencia_voucher}` : '') +
            '\nClick para ampliar'
          }>
            <Box
              onClick={() => onVerVoucher(primerVoucher)}
              sx={{
                width: 44, height: 44, borderRadius: 1,
                bgcolor: '#f1f5f9', border: '1px solid #cbd5e1',
                overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                transition: 'transform 0.15s',
                '&:hover': { transform: 'scale(1.06)', borderColor: '#0d9488' },
              }}
            >
              {primerVoucher.url ? (
                <img src={primerVoucher.url} alt="voucher"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Box sx={{
                  width: '100%', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, color: '#94a3b8',
                }}>
                  s/img
                </Box>
              )}
            </Box>
          </Tooltip>
          {primerVoucher.referencia_voucher && (
            <Typography sx={{ fontSize: 10, color: '#64748b',
                               fontFamily: 'monospace', wordBreak: 'break-all' }}>
              ref: {primerVoucher.referencia_voucher}
            </Typography>
          )}
          {otrosVouchers.length > 0 && (
            <Tooltip title={`${otrosVouchers.length} voucher(s) más asociado(s)`}>
              <Chip
                label={`+${otrosVouchers.length} voucher${otrosVouchers.length === 1 ? '' : 's'}`}
                size="small"
                onClick={() => onVerVoucher(otrosVouchers[0])}
                sx={{ height: 18, fontSize: 9, cursor: 'pointer',
                      bgcolor: '#e0e7ff', color: '#3730a3' }}
              />
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}

/** Lightbox simple para ver el voucher completo. */
function VoucherLightbox({ voucher, onClose }) {
  if (!voucher) return null;
  return (
    <Dialog open={!!voucher} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 700, py: 1.5 }}>
        Voucher #{voucher.id} · {voucher.tipo || 'sin tipo'}
        {voucher.monto_voucher && ` · ${fmt(voucher.monto_voucher)}`}
        {voucher.referencia_voucher && (
          <Box component="span" sx={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
            {' '}· ref {voucher.referencia_voucher}
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: '#0f172a', textAlign: 'center' }}>
        {voucher.url ? (
          <img
            src={voucher.url}
            alt="voucher"
            style={{ maxWidth: '100%', maxHeight: '75vh', display: 'block', margin: '0 auto' }}
          />
        ) : (
          <Typography sx={{ color: '#94a3b8', p: 4 }}>
            Este voucher no tiene imagen adjunta.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {voucher.url && (
          <Button onClick={() => window.open(voucher.url, '_blank')}
            sx={{ textTransform: 'none' }}>
            Abrir en nueva pestaña
          </Button>
        )}
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
